import { z } from "zod"

/**
 * @description
 *	The schema of {@link Branding} type.
 *
 * @remarks
 *	This schema mirrors the output of the GET /branding/:integrationId
 *	endpoint of the Biometry backend. The accent color is nullable instead of
 *	optional, because the backend always emits the attribute.
 *
 *	The name is only used as the alternative text of the logo, for the screen
 *	readers: it must never be rendered as a visible text, because it is a free
 *	text shown on the page where the human being delivers its face.
 *
 *	The contrast of the accent color is guarded by the backend on write, and
 *	only there: an item written directly over its table skips that gate and
 *	is served as it is, so an accent that arrives here is well formed but not
 *	necessarily legible.
 */
export const BrandingSchema = z.object({
	accentColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/)
		.nullable(),
	logoPath: z.string().regex(/^\/branding\/[A-Za-z0-9][A-Za-z0-9._-]{0,118}\.(png|webp)$/),
	name: z.string().trim().min(1).max(64),
	showInstructions: z.boolean()
})

/**
 * @description
 *	The branding of an integration, shown to the human being that is being
 *	verified.
 */
export type Branding = z.infer<typeof BrandingSchema>
